import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ApiClientError } from '../api/client';
import { endpoints } from '../api/endpoints';
import { useAuth } from '../auth/AuthProvider';

/**
 * Client-side policy mirrors the server contract exactly.
 *
 * These are restated rather than imported because `@oip/contracts` compiles to
 * CommonJS: the bundler cannot statically resolve a named runtime export
 * through its `__exportStar` re-exports, so only TYPES are importable in a
 * production build. `ChangePasswordDialog.policy.test.ts` imports the real
 * contract values and asserts these match, so drift fails a test rather than
 * shipping. (Emitting ESM from the contracts package would remove the
 * duplication — a build-system change, out of scope for this stage.)
 *
 * The byte ceiling is not cosmetic: bcrypt hashes only the first 72 bytes and
 * discards the rest, so a longer password would be silently truncated. Length
 * is therefore measured in BYTES, matching the server.
 */
export const NEW_PASSWORD_MIN_LENGTH = 12;
export const BCRYPT_MAX_BYTES = 72;

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z
      .string()
      .min(NEW_PASSWORD_MIN_LENGTH, `At least ${NEW_PASSWORD_MIN_LENGTH} characters`),
    confirmNewPassword: z.string().min(1, 'Re-enter the new password'),
  })
  .refine((v) => new TextEncoder().encode(v.newPassword).length <= BCRYPT_MAX_BYTES, {
    path: ['newPassword'],
    message: `At most ${BCRYPT_MAX_BYTES} bytes (some characters count as more than one)`,
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ['newPassword'],
    message: 'New password must differ from the current one',
  })
  .refine((v) => v.newPassword === v.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Passwords do not match',
  });

type FormValues = z.infer<typeof schema>;

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { endSession } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  /** Clears every field on close so no password lingers in component state. */
  const close = () => {
    reset();
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      // `confirmNewPassword` exists only in the UI. The request schema is
      // .strict(), so sending it would be a 400 — it is destructured away here
      // rather than filtered later, which makes the omission impossible to undo
      // by accident.
      await endpoints.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      // Success invalidates every token for this user, including the one that
      // made this request. There is nothing to keep, so end the session and let
      // RequireAuth route to /login with the reason.
      endSession('password-changed');
      close();
    } catch (e) {
      if (!(e instanceof ApiClientError)) {
        setFormError('Could not change your password. Try again.');
        return;
      }

      // 401 here means the CURRENT password was wrong, not that the session
      // expired — the request opted out of global session-expiry handling.
      if (e.status === 401) {
        setError('currentPassword', { message: 'Current password is incorrect' });
        return;
      }

      if (e.status === 400) {
        // Map field-level validation onto the field the server named.
        const details = e.details ?? [];
        let mapped = false;
        for (const d of details) {
          if (d.path === 'newPassword' || d.path === 'currentPassword') {
            setError(d.path, { message: d.message });
            mapped = true;
          }
        }
        if (!mapped) setFormError(e.message);
        return;
      }

      setFormError(e.correlationId ? `${e.message} (reference ${e.correlationId})` : e.message);
    }
  });

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : close} maxWidth="xs" fullWidth>
      <form onSubmit={onSubmit} noValidate>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              You will be signed out of every device and will need to sign in again.
            </Typography>

            {formError && <Alert severity="error">{formError}</Alert>}

            <TextField
              {...register('currentPassword')}
              label="Current password"
              type="password"
              autoComplete="current-password"
              autoFocus
              fullWidth
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              inputProps={{ 'aria-label': 'Current password' }}
            />
            <TextField
              {...register('newPassword')}
              label="New password"
              type="password"
              autoComplete="new-password"
              fullWidth
              error={!!errors.newPassword}
              helperText={
                errors.newPassword?.message ??
                `At least ${NEW_PASSWORD_MIN_LENGTH} characters, at most ${BCRYPT_MAX_BYTES} bytes.`
              }
              inputProps={{ 'aria-label': 'New password' }}
            />
            <TextField
              {...register('confirmNewPassword')}
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              fullWidth
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword?.message}
              inputProps={{ 'aria-label': 'Confirm new password' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          {/* Disabled while pending — the endpoint is not idempotent. */}
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Changing…' : 'Change password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
